import type { CSSProperties, ElementType } from 'react'
import { memo, useMemo } from 'react'

import { motion } from 'framer-motion'

export interface TextShimmerProps {
  children: string
  as?: ElementType
  duration?: number
  spread?: number
  className?: string
}

const ShimmerComponent = ({
  children,
  as: Component = 'span',
  duration = 2,
  spread = 2,
  className,
}: TextShimmerProps) => {
  const MotionComponent = motion.create<HTMLSpanElement>(Component as string)

  const dynamicSpread = useMemo(
    () => (children?.length ?? 0) * spread,
    [children, spread],
  )

  return (
    <MotionComponent
      animate={{ backgroundPosition: '0% center' }}
      initial={{ backgroundPosition: '100% center' }}
      transition={{
        duration,
        ease: 'linear',
        repeat: Number.POSITIVE_INFINITY,
      }}
      style={
        {
          display: 'inline-block',
          backgroundSize: '250% 100%, auto',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          backgroundRepeat: 'no-repeat, padding-box',
          '--spread': `${dynamicSpread}px`,
          backgroundImage: [
            'linear-gradient(90deg, transparent calc(50% - var(--spread)), var(--chakra-colors-bg) 50%, transparent calc(50% + var(--spread)))',
            'linear-gradient(var(--chakra-colors-fg-muted), var(--chakra-colors-fg-muted))',
          ].join(', '),
        } as CSSProperties
      }
      className={className}
    >
      {children}
    </MotionComponent>
  )
}

export const Shimmer = memo(ShimmerComponent)
